import {
  ApiRoutes,
  PUBLIC_API_PREFIX,
  VocabAudioResponseSchema,
  type DashboardStats,
  type DueFlashcardsResponse,
  type Flashcard,
  type Rating,
  type ReadingPassage,
  type UpdateUserSettingsRequest,
  type User,
  type UserSettings,
  type VocabEntryWithTargets,
  type VocabListResponse,
  type WritingFeedback,
  type WritingFeedbackRequest,
  type WritingPrompt,
} from "@engo/shared";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${PUBLIC_API_PREFIX}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const msg =
      typeof data === "object" &&
      data !== null &&
      "error" in data &&
      typeof (data as { error?: { message?: string } }).error?.message === "string"
        ? (data as { error: { message: string } }).error.message
        : res.statusText;
    throw new ApiError(msg, res.status, data);
  }
  return data as T;
}

export const api = {
  me: () => apiJson<User>(ApiRoutes.authMe),
  listVocab: (query: Record<string, string | number | undefined>) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === "") continue;
      params.set(k, String(v));
    }
    const path = `${ApiRoutes.vocab}${params.size > 0 ? `?${params.toString()}` : ""}`;
    return apiJson<VocabListResponse>(path);
  },
  updateVocab: (id: string, body: unknown) =>
    apiJson<VocabEntryWithTargets>(`${ApiRoutes.vocab}/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  syncVocab: (id: string, failedOnly = true) =>
    apiJson<VocabEntryWithTargets>(`${ApiRoutes.vocab}/${id}/sync`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ failed_only: failedOnly }),
    }),
  fetchVocabAudio: async (word: string) => {
    const params = new URLSearchParams({ word });
    const data = await apiJson<unknown>(`${ApiRoutes.vocabAudio}?${params.toString()}`);
    return VocabAudioResponseSchema.parse(data);
  },
  getDueFlashcards: (limit = 20) =>
    apiJson<DueFlashcardsResponse>(`${ApiRoutes.flashcardsDue}?limit=${limit}`),
  reviewFlashcard: (id: string, rating: Rating) =>
    apiJson<Flashcard>(`/v1/flashcards/${id}/review`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rating }),
    }),
  generateReading: () => apiJson<ReadingPassage>(ApiRoutes.practiceReading, { method: "POST" }),
  generateWritingPrompt: () =>
    apiJson<WritingPrompt>(ApiRoutes.practiceWritingPrompt, { method: "POST" }),
  gradeWriting: (body: WritingFeedbackRequest) =>
    apiJson<WritingFeedback>(ApiRoutes.practiceWritingFeedback, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  getDashboardStats: () => apiJson<DashboardStats>(ApiRoutes.statsDashboard),
  getSettings: () => apiJson<UserSettings>(ApiRoutes.settings),
  updateSettings: (patch: UpdateUserSettingsRequest) =>
    apiJson<UserSettings>(ApiRoutes.settings, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }),
};
